import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Button,
  Stepper,
  Step,
  StepLabel,
  Chip,
  TextField,
} from '@mui/material';
import {
  PlayArrow,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
} from '@mui/icons-material';
import { useProcess } from '../contexts/ProcessContext';

const steps = [
  'Upload Files',
  'Process Documentation',
  'Process Regulations',
  'Retrieve Data',
  'LLM Analysis',
  'Complete'
];

const ProcessStatus: React.FC = () => {
  const { status, startProcess } = useProcess();
  const [clientName, setClientName] = useState('');

  const getActiveStep = () => {
    if (status.error) return -1;

    // If process is done, always return final step
    if (status.done) return 5;

    // If not running and not done, return initial step
    if (!status.running) return 0;

    const step = status.current_step?.toLowerCase() || '';

    // Map backend steps to UI steps precisely
    if (step.includes('complete') || step.includes('analysis complete')) return 5;
    if (step.includes('running llm') || step.includes('llm analysis')) return 4;
    if (step.includes('retrieving') || step.includes('retrieve')) return 3;
    if (step.includes('processing regulation') || step.includes('regulation')) return 2;
    if (step.includes('processing documentation') || step.includes('documentation')) return 1;

    // Use progress to determine step if step text is unclear
    if (status.progress >= 80) return 4;
    if (status.progress >= 60) return 3;
    if (status.progress >= 40) return 2;
    if (status.progress >= 20) return 1;

    return 0;
  };

  const getElapsedTime = () => {
    if (!status.start_time) return '';
    
    const start = new Date(status.start_time);
    const end = status.end_time ? new Date(status.end_time) : new Date();
    const diff = Math.floor((end.getTime() - start.getTime()) / 1000);
    
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    
    return `${minutes}m ${seconds}s`;
  };

  const handleStartProcess = async () => {
    if (!clientName.trim()) {
      alert('Please enter a client name');
      return;
    }
    await startProcess(clientName.trim());
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {status.running ? (
          <Refresh sx={{ animation: 'spin 1s linear infinite' }} />
        ) : status.done ? (
          <CheckCircle color="success" />
        ) : status.error ? (
          <ErrorIcon color="error" />
        ) : (
          <PlayArrow />
        )}
        Process Status
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Stepper activeStep={getActiveStep()} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} completed={status.done && index < steps.length - 1}>
              <StepLabel
                error={Boolean(status.error && getActiveStep() === index)}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {status.running && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              {status.current_step || 'Processing...'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {status.progress}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={status.progress} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          {status.start_time && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Elapsed: {getElapsedTime()}
            </Typography>
          )}
        </Box>
      )}

      {status.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Error:</strong> {status.error}
          </Typography>
        </Alert>
      )}

      {status.done && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              Analysis completed successfully! Results are now available.
            </Typography>
            {status.start_time && status.end_time && (
              <Chip
                label={`Completed in ${getElapsedTime()}`}
                color="success"
                size="small"
              />
            )}
          </Box>
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          label="Client Name"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Enter client name"
          variant="outlined"
          size="medium"
          disabled={status.running}
          required
          helperText="Required for compliance analysis"
          sx={{ minWidth: 300 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={handleStartProcess}
          disabled={status.running || !clientName.trim()}
          startIcon={<PlayArrow />}
        >
          {status.running ? 'Processing...' : 'Run Analysis'}
        </Button>

        {status.done && (
          <Chip
            label="Results Available"
            color="success"
            variant="outlined"
          />
        )}
      </Box>
    </Paper>
  );
};

export default ProcessStatus;